/**
 * TreeNodeTitle  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";

import { BEM } from "@adapptio/lib-components";

import { isFunction, isUndefined } from "@adapptio/lib-shared";

import {
	ITreeNode,
	TTreeViewNodeActionClickHandler,
	TTreeViewNodeActionMenuItemClickHandler,
	TTreeViewNodeClickHandler,
	TTreeViewNodeFlagClickHandler
} from "./TreeViewTypes";
import { Icon, ICON_SIZE } from "../Icon";
import { Button } from "../Button/Button";
import { IDropdownMenuItem } from "../DropdownMenu";

export interface ITreeNodeTitleProps {
	node: ITreeNode;
	onClick?: TTreeViewNodeClickHandler;
	onFlagClick?: TTreeViewNodeFlagClickHandler;
	onActionClick?: TTreeViewNodeActionClickHandler;
	onActionMenuItemClick?: TTreeViewNodeActionMenuItemClickHandler;
	getNodeClassName?: (node: ITreeNode) => string;
}

export const TreeNodeTitle: React.FC<ITreeNodeTitleProps> = ({
	node,
	onFlagClick,
	onActionClick,
	onActionMenuItemClick,
	getNodeClassName,
	onClick
}) => {
	const { label, value, flags, description, actions } = node;

	const [ menuOpenedCount, setMenuOpenedCount ] = useState(0);

	const baseClass = BEM("edt-tree-treenode-title");

	if (isFunction(getNodeClassName)) {
		const className = getNodeClassName(node);

		if (className) {
			baseClass.add(className);
		}
	}

	if (menuOpenedCount > 0) {
		baseClass.mod("has-action-menu-opened");
	}

	const handleOnClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (isFunction(onClick)) {
				onClick(node, event);
			}
		},
		[ onClick, node ]
	);

	const flagClickHandlers = useMemo(() => {
		return (flags || []).map((flag) => () => {
			if (isFunction(flag.onClick)) {
				flag.onClick();
			}

			if (isFunction(onFlagClick)) {
				onFlagClick(node, flag);
			}
		});
	}, [ onFlagClick, actions ]);

	const actionClickHandlers = useMemo(() => {
		return (actions || []).map((action) => () => {
			if (isFunction(action.onClick)) {
				action.onClick(node, action);
			}

			if (isFunction(onActionClick)) {
				onActionClick(node, action);
			}
		});
	}, [ onActionClick, actions, node ]);

	const actionMenuItemClickHandlers = useMemo(() => {
		return (actions || []).map((action) => (menuItem: IDropdownMenuItem) => {
			if (isFunction(onActionMenuItemClick)) {
				onActionMenuItemClick(node, action, menuItem);
			}
		});
	}, [ onActionMenuItemClick, actions ]);

	const onMenuOpenedChange = useCallback((isMenuOpened: boolean) => {
		if (isMenuOpened) {
			setMenuOpenedCount((x) => x + 1);
		} else {
			setMenuOpenedCount((x) => Math.max(0, x - 1));
		}
	}, []);

	return (
		<div className={baseClass.toClassName()} onClick={handleOnClick}>
			<div className={baseClass.element("label").toClassName()} title={label}>
				{label}
				{!isUndefined(value) && ":"}
			</div>
			{!isUndefined(value) && (
				<div className={baseClass.element("value").toClassName()} title={String(value)}>
					{value}
				</div>
			)}
			{flags?.length > 0 &&
				flags.map((flag, index) => {
					const flagClass = baseClass.element("flag");

					if (flag.className) {
						flagClass.add(flag.className);
					}

					return (
						<div
							key={`flag-${index}`}
							className={flagClass.toClassName()}
							// @todo Implement tooltip as a <Tooltip .../> component
							title={flag.tooltip}
							onClick={flagClickHandlers[index]}
						>
							<Icon name={flag.icon} size={ICON_SIZE.XSMALL} />
						</div>
					);
				})}
			<div className={baseClass.element("spacer").toClassName()} />
			{description && (
				<div className={baseClass.element("description").toClassName()}>{description}</div>
			)}
			{actions?.length > 0 &&
				actions.map((action, index) => {
					const actionClass = BEM("edt-tree-action", null, {
						style: action.style
					});

					if (action.className) {
						actionClass.add(action.className);
					}

					return (
						<Button
							key={`action-${index}`}
							icon={action.icon}
							tooltip={action.tooltip}
							isLoading={action.isLoading}
							showLoading={action.showLoading}
							disabled={action.disabled}
							active={action.active}
							menuItems={action.menuItems}
							menuAnchor={action.menuAnchor}
							menuDirection={action.menuDirection}
							menuSize={action.menuSize}
							menuStyle={action.menuStyle}
							tooltipAnchor={action.tooltipAnchor}
							baseClass={actionClass}
							onClick={
								action.hasPrimaryAction !== false ? actionClickHandlers[index] : undefined
							}
							onMenuItemClick={actionMenuItemClickHandlers[index]}
							onMenuOpenedChange={onMenuOpenedChange}
							hideMenuArrow={action.hideMenuArrow}
						/>
					);
				})}
		</div>
	);
};
